import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, CardBody, CardHeader, Table } from '@edgebox/react-components';
import { useNavigate, useParams } from 'react-router-dom';
import { ExternalServiceId, InternalId } from '@edgebox/data-definition-kit';
import {
  AccessScope,
  AiProviderApiType,
  AiProviderAuthenticationType,
  AiProviderStatus,
  ServiceSecretStatus,
  ServiceSecretTargetType,
} from '@edgebox/sync-core-data-definitions';
import { ClientAiProviderDraft, ClientAiProviderEntity, type ClientServiceSecretEntity } from '@edgebox/sync-core-rest-client';
import { formatDateTime, renderValue, useSyncCoreApi } from '../aiHelpers';
import { DetailsCard } from '../components/DetailsCard';
import { ErrorCard } from '../components/ErrorCard';
import { LoadingCard } from '../components/LoadingCard';
import { RawDataCard } from '../components/RawDataCard';
import { ScreenHeader } from '../components/ScreenHeader';
import { AiProviderDraftEditor, AiProviderEntityEditor } from '../components/AiProviderForm';
import { AiProviderServiceSecretEditor } from '../components/AiProviderServiceSecretForm';

function isNewId(id: string | undefined): boolean {
  return !id || id === 'new';
}

function createDefaultProviderDraft(): ClientAiProviderDraft {
  return new ClientAiProviderDraft({
    accessScope: AccessScope.Project,
    authenticationType: AiProviderAuthenticationType.ServiceSecret,
    status: AiProviderStatus.Draft,
    name: '',
    apiType: AiProviderApiType.AwsBedrock,
    baseUrl: undefined,
    region: undefined,
    apiVersion: undefined,
    defaultTimeoutMs: 60_000,
    maxTimeoutMs: 120_000,
    customer: undefined,
    project: undefined,
  });
}

export function AiProviderDetailView() {
  const api = useSyncCoreApi();
  const params = useParams();
  const navigate = useNavigate();
  const idParam = params.id;

  const creating = isNewId(idParam);
  const id = !creating ? (idParam as InternalId) : undefined;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(undefined);
  const [entity, setEntity] = useState<ClientAiProviderEntity | undefined>(undefined);
  const [showEdit, setShowEdit] = useState<boolean>(creating);

  const [secretsLoading, setSecretsLoading] = useState(false);
  const [secretsError, setSecretsError] = useState<unknown>(undefined);
  const [secrets, setSecrets] = useState<ClientServiceSecretEntity[]>([]);
  const [showSecretEditor, setShowSecretEditor] = useState(false);
  const [secretSaved, setSecretSaved] = useState(false);

  const draft = useMemo(() => (creating ? createDefaultProviderDraft() : undefined), [creating]);

  useEffect(() => {
    if (!api || !id) return;
    let cancelled = false;
    setLoading(true);
    setError(undefined);

    api.ai.providers
      .item(id)
      .then((e) => {
        if (cancelled) return;
        setEntity(e);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, id]);

  useEffect(() => {
    if (!api || !id) return;
    let cancelled = false;
    setSecretsLoading(true);
    setSecretsError(undefined);

    // The service-secrets API uses ExternalServiceId for the generic `targetId` type.
    // AI providers use InternalId, but the endpoint accepts the provider ID string.
    const targetId = id as unknown as ExternalServiceId;

    api.authentication.serviceSecrets
      .list(
        { page: 0, itemsPerPage: 50 },
        {
          targetType: ServiceSecretTargetType.AiProvider,
          targetId,
          status: [ServiceSecretStatus.Draft, ServiceSecretStatus.Active, ServiceSecretStatus.Archived],
        }
      )
      .then((res) => {
        if (cancelled) return;
        setSecrets(res.items ?? []);
      })
      .catch((e) => {
        if (cancelled) return;
        setSecretsError(e);
      })
      .finally(() => {
        if (cancelled) return;
        setSecretsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, id]);

  if (!api) return <Alert variant="warning">Missing API.</Alert>;
  if (!creating && !id) return <Alert variant="warning">Missing provider id.</Alert>;

  const headerTitle = creating ? 'Create provider' : 'Provider details';

  return (
    <>
      <ScreenHeader
        title={headerTitle}
        onBack={() => navigate('/ai/provider')}
        right={
          !creating && entity ? (
            <Button variant={showEdit ? 'light' : 'primary'} onClick={() => setShowEdit((v) => !v)}>
              {showEdit ? 'Cancel edit' : 'Edit'}
            </Button>
          ) : undefined
        }
      />

      {loading ? <LoadingCard text="Loading provider…" /> : null}
      {error ? <ErrorCard error={error} /> : null}

      {creating && draft ? (
        <Card className="mb-3">
          <CardHeader>Provider</CardHeader>
          <CardBody>
            <AiProviderDraftEditor
              api={api}
              initialValues={draft}
              submitLabel="Create"
              onSubmit={async (values) => {
                const created = await api.ai.providers.create(values);
                setEntity(created);
                navigate(`/ai/provider/${created.id}`, { replace: true });
              }}
            />
          </CardBody>
        </Card>
      ) : null}

      {!creating && entity ? (
        <>
          {!showEdit ? (
            <>
              <DetailsCard
                title="Overview"
                fields={[
                  { label: 'ID', value: renderValue(entity.id) },
                  { label: 'Name', value: renderValue(entity.name) },
                  {
                    label: 'Status',
                    value: <Badge bg={entity.status === AiProviderStatus.Active ? 'success' : 'secondary'}>{entity.status}</Badge>,
                  },
                  { label: 'Access scope', value: renderValue(entity.accessScope) },
                  { label: 'Frozen', value: renderValue(entity.frozen) },
                  { label: 'Customer', value: renderValue(entity.customer?.getId?.()) },
                  { label: 'Project', value: renderValue(entity.project?.getId?.()) },
                  { label: 'Created', value: renderValue(formatDateTime(entity.createdAt)) },
                  { label: 'Updated', value: renderValue(formatDateTime(entity.updatedAt)) },
                ]}
              />

              <DetailsCard
                title="Provider configuration"
                fields={[
                  { label: 'API type', value: renderValue(entity.apiType) },
                  { label: 'Authentication', value: renderValue(entity.authenticationType) },
                  { label: 'Base URL', value: renderValue(entity.baseUrl) },
                  { label: 'Region', value: renderValue(entity.region) },
                  { label: 'API version', value: renderValue(entity.apiVersion) },
                  { label: 'Default timeout', value: renderValue(entity.defaultTimeoutMs) },
                  { label: 'Max timeout', value: renderValue(entity.maxTimeoutMs) },
                ]}
              />

              <Card className="mb-3">
                <CardHeader className="d-flex align-items-center justify-content-between">
                  <div>Service secrets</div>
                  <Button
                    size="sm"
                    variant={showSecretEditor ? 'light' : 'primary'}
                    onClick={() => {
                      setShowSecretEditor((v) => !v);
                      setSecretSaved(false);
                    }}
                  >
                    {showSecretEditor ? 'Cancel' : 'Update secret'}
                  </Button>
                </CardHeader>
                <CardBody>
                  {entity.authenticationType !== AiProviderAuthenticationType.ServiceSecret ? (
                    <Alert variant="warning">
                      This provider is not configured to use service secrets (authentication type is{' '}
                      <strong>{entity.authenticationType}</strong>).
                    </Alert>
                  ) : null}

                  {secretSaved ? <Alert variant="success">Secret updated.</Alert> : null}

                  {secretsLoading ? <div className="text-muted">Loading secrets…</div> : null}
                  {secretsError ? <ErrorCard error={secretsError} /> : null}

                  <Table striped bordered hover size="sm" responsive className="mb-3">
                    <thead>
                      <tr>
                        <th>Version</th>
                        <th>Status</th>
                        <th>Name</th>
                        <th>Key name</th>
                        <th>Key set at</th>
                        <th>Key archived at</th>
                      </tr>
                    </thead>
                    <tbody>
                      {secrets.map((s) => (
                        <tr key={s.id}>
                          <td>{s.version ?? '-'}</td>
                          <td>
                            <Badge bg={s.status === ServiceSecretStatus.Active ? 'success' : 'secondary'}>{s.status}</Badge>
                          </td>
                          <td>{s.name}</td>
                          <td>{s.accessKeyName ?? '-'}</td>
                          <td>{formatDateTime(s.accessKeySetAt) ?? '-'}</td>
                          <td>{formatDateTime(s.accessKeyArchivedAt) ?? '-'}</td>
                        </tr>
                      ))}
                      {!secrets.length && !secretsLoading ? (
                        <tr>
                          <td colSpan={6} className="text-muted">
                            No secrets found.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </Table>

                  {showSecretEditor ? (
                    <AiProviderServiceSecretEditor
                      initialValues={{
                        name: entity.name,
                        accessKeyName: '',
                        accessKeyEncrypted: '',
                      }}
                      submitLabel="Save secret"
                      onSubmit={async (values) => {
                        setSecretSaved(false);
                        const updated = await api.authentication.serviceSecrets.setAiProviderAccessKey(entity.id, values);
                        setSecrets((prev) => {
                          const next = [updated, ...prev.filter((p) => p.id !== updated.id)];
                          return next;
                        });
                        setSecretSaved(true);
                        setShowSecretEditor(false);
                      }}
                    />
                  ) : null}
                </CardBody>
              </Card>

              <RawDataCard entity={entity} />
            </>
          ) : (
            <Card className="mb-3">
              <CardHeader>Edit provider</CardHeader>
              <CardBody>
                <AiProviderEntityEditor
                  api={api}
                  initialValues={entity}
                  submitLabel="Save"
                  onSubmit={async (values) => {
                    const updated = await api.ai.providers.update(values);
                    setEntity(updated);
                    setShowEdit(false);
                  }}
                />
              </CardBody>
            </Card>
          )}
        </>
      ) : null}
    </>
  );
}
